import Image, { ImageProps } from 'next/image';
import { ReactNode } from 'react';

interface NavGroup {
  title: string;
  items: Array<{
    label: string;
    path: string;
    external?: boolean;
  }>;
}
export interface FooterProps {
  shopMessage: string;
  copyrightMessage: ReactNode;
  links: NavGroup[];
  logo: ImageProps;
}

export function Footer({ logo, copyrightMessage }: FooterProps) {
  return (
    <footer className="print:hidden bg-footer" aria-labelledby="footerHeading">
      <h2 id="footerHeading" className="sr-only">
        Footer
      </h2>
      <div className="max-w-7xl mx-auto pb-4 px-4 sm:px-6 lg:px-8">
        <div className="border-t border-gray-200 pt-4 md:flex md:items-center md:justify-between">
          <div className="">
            <Image
              src="/channels.svg"
              alt="Resal channels"
              width={150}
              height={80}
              layout="intrinsic"
              unoptimized
            />
          </div>
          <div className="flex space-i-6 md:order-2 justify-center md:justify-start items-center">
            <a
              href="https://maroof.sa/11590"
              className="text-gray-400 hover:text-gray-500"
              target="_blank"
              rel="noreferrer noopener"
            >
              <span className="sr-only">Maroof</span>
              <Image
                src="/maroof.png"
                alt="Maroof Badge"
                width={32}
                height={15}
                layout="intrinsic"
                unoptimized
              />
            </a>

            <a
              href="https://s3.amazonaws.com/s3.resal.me/VAT+Certification.pdf"
              className="text-gray-400 hover:text-gray-500"
              target="_blank"
              rel="noreferrer noopener"
            >
              <span className="sr-only">VAT</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 17 21"
                className="h-6 w-6"
              >
                <g fillRule="nonzero" fill="none">
                  <path
                    fill="#005B47"
                    d="M.8 14.7h15.4c.3 0 .6.1.8.3V.9c0-.5-.4-.9-.9-.9H1C.4 0 0 .4 0 .9V15c.2-.2.5-.3.8-.3"
                  />
                  <path
                    fill="#FFF"
                    d="M15 1.6c0-.1-.1-.3-.3-.3l-.3.3.3.3c.2 0 .3-.2.3-.3m-1.1 2.1h1.7v-.6-.3l-.5-.2H14v1.1zM10.5 5h-.3v-.4H10.6c.1 0 .2 0 .2-.4V2h.4v1.7h2.4V2h.4v.2H15c.3 0 .5.1.7.3l.2.6v1h-5L11 5l-.4.1zm-.7-.3c0-.1 0-.2-.2-.2s-.3 0-.3.2c0 .1.1.3.3.3l.2-.3m-.7 0c0-.1 0-.2-.2-.2s-.3 0-.3.2c0 .1.1.3.3.3l.2-.3M7 4.7c0-.1-.1-.3-.3-.3a.3.3 0 100 .6c.2 0 .3-.2.3-.3M3.1 1.2S3.1 1 3 1s-.3.1-.3.3c0 0 .1.2.3.2.1 0 .2-.1.2-.3m.7 0s0-.2-.2-.2-.3.1-.3.3c0 0 .1.2.3.2.1 0 .2-.1.2-.3m-.7 1.2c-.3 0-.7.2-.7.7 0 .4.4.6.7.6h.3V2.4H3zm.1 1.8C2.3 4.2 2 3.6 2 3 2 2.4 2.5 2 3.3 2v-.2h.5v2h2.7V2h.3v1.7h2.5V2h.4v2.2H3.2zM15 5h-.4v3.2h.4V5zM2.4 6.4v1.4H2c-.3 0-.7-.2-.7-.7 0-.4.4-.7.7-.7h.3zm9.2 0v1.4H11a.8.8 0 01-.4-.7c0-.4.4-.7.7-.7h.3zM6 7.4c0 .4-.3.7-.7.7-.4 0-.7-.3-.7-.7 0-.4.3-.8.7-.8.4 0 .7.4.7.8m.1.8H14V5h-.4v2.8H12V6h-.6c-.7 0-1.2.5-1.2 1.1 0 .3 0 .5.2.7H8.7V6h-.4v1.8H6.5v-.4c0-.7-.5-1.2-1-1.2-.7 0-1.2.5-1.2 1.2v.4H2.9v-2h-.4V6h-.1C1.5 6 1 6.5 1 7.1c0 .5.3 1.1 1.3 1.1h2.3c.2.2.5.3.8.3.3 0 .6 0 .8-.3M12 5.4c0-.1-.1-.2-.3-.2l-.2.2c0 .2 0 .3.2.3s.3-.1.3-.3m-.7 0c0-.1-.1-.2-.3-.2l-.2.2c0 .2 0 .3.2.3s.3-.1.3-.3M9 8.7s-.1-.2-.3-.2l-.2.2c0 .2 0 .3.2.3s.3-.1.3-.3m-.7 0L8 8.5l-.3.2c0 .2.2.3.3.3.1 0 .3-.1.3-.3M2 5.3L2 5c-.2 0-.3.2-.3.3 0 .1.1.2.3.2.1 0 .2 0 .2-.2m.5.2a.2.2 0 01-.3-.2c0-.1.1-.3.3-.3l.2.3c0 .1 0 .2-.2.2M16 10h-.4v2.7h.4V10zm-7.5 1.4h1.3l.3.1.1.3v.6H8.5v-1zm4.7.6c0 .4-.3.7-.7.7-.4 0-.8-.3-.8-.7 0-.3.4-.6.7-.6.5 0 .8.3.8.6m0 .7H15V10h-.4v2.4h-1V12c0-.5-.4-1-1-1-.7 0-1.1.5-1.1 1v.3h-.8v-.5c0-.2 0-.4-.2-.5-.1-.2-.3-.2-.6-.2H8.5v-.2h-.4v1.5h-1V10h-.3v2.7h4.9c.2.2.5.3.8.3.3 0 .6-.1.8-.3m-3.7-2.2s-.1-.2-.3-.2l-.2.2c0 .2 0 .3.2.3s.3-.1.3-.3m-3.9-.1c0-.1 0-.2-.2-.2s-.3 0-.3.2c0 .1.1.2.3.2l.2-.2m-2.3.8v1.2H3c-.3 0-.7-.2-.7-.6 0-.4.4-.6.7-.6h.3zm2.4 0v1.2h-.6c-.2-.2-.4-.4-.4-.6 0-.3.3-.6.7-.6h.3zm.4 1.5V11h-.6c-.7 0-1.2.3-1.2.9 0 .2 0 .4.2.5h-.8v-1.6h-.4v.2h-.1c-.8 0-1.3.3-1.3.9 0 .4.3.9 1.3.9h2.9zm-3-2.4c0-.2-.2-.3-.3-.3-.2 0-.3.1-.3.3 0 0 .1.2.3.2l.2-.2m.5.2c-.2 0-.3-.1-.3-.2 0-.2.1-.3.3-.3.1 0 .2.1.2.3 0 0 0 .2-.2.2"
                  />
                  <path
                    fill="#D4A007"
                    d="M16.1 21H1c-.5 0-.9-.4-.9-.8v-4.4c0-.4.4-.8.9-.8H16c.5 0 .9.4.9.8v4.4c0 .4-.4.8-.9.8"
                  />
                  <path
                    fill="#FFF"
                    d="M3.5 18.8l1-3.8H6l-1.5 5h-2L1 15h1.5l1 3.8zM16 15h-5l.4 1.1H13V20h1.4v-3.9H16z"
                  />
                  <path
                    fill="#005B47"
                    d="M11 17.3l-.2-.3c-.8.5-2.1 1.9-2.1 1.9l-.5-.5-.2.5.7 1.1c1.2-1.9 2.3-2.7 2.3-2.7"
                  />
                  <path
                    fill="#FFF"
                    d="M8.5 16.2l.5 1.7 1-1.1a93 93 0 00-.5-1.8h-2L6 20h1.5l1-3.8zm.5 3.1l.2.7H11l-.8-2L9 19.3"
                  />
                </g>
              </svg>
            </a>
          </div>
          <p className="mt-4 text-sm text-gray-400 md:mt-0 md:order-1 text-center md:text-start">
            {copyrightMessage}
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
